import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Flame, MousePointer2, ScrollText, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface HeatmapData {
  x_bucket: number;
  y_bucket: number;
  event_type: string;
  interaction_count: number;
}

interface PageOption {
  path: string;
  views: number;
}

export function HeatmapVisualization() {
  const [pages, setPages] = useState<PageOption[]>([]);
  const [selectedPage, setSelectedPage] = useState<string>('');
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([]);
  const [eventType, setEventType] = useState<'click' | 'mousemove' | 'all'>('click');
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const loadPages = async () => {
    try {
      const { data, error } = await supabase
        .from('visitor_pageviews')
        .select('page_path')
        .order('viewed_at', { ascending: false })
        .limit(1000);

      if (error) throw error;

      // Count page views
      const pageCounts = data.reduce(
        (acc, item) => {
          acc[item.page_path] = (acc[item.page_path] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      );

      const pageOptions = Object.entries(pageCounts)
        .map(([path, views]) => ({ path, views }))
        .sort((a, b) => b.views - a.views)
        .slice(0, 20);

      setPages(pageOptions);
      if (pageOptions.length > 0 && !selectedPage) {
        setSelectedPage(pageOptions[0].path);
      }
    } catch (error: any) {
      console.error('Error loading pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const loadHeatmapData = async () => {
    if (!selectedPage) return;

    try {
      setRendering(true);

      const { data, error } = await supabase.rpc('get_page_heatmap', {
        p_page_path: selectedPage,
        p_event_type: eventType === 'all' ? null : eventType,
        p_days: 30,
      });

      if (error) throw error;
      setHeatmapData(data || []);
    } catch (error: any) {
      console.error('Error loading heatmap:', error);
      toast.error('Failed to load heatmap data');
    } finally {
      setRendering(false);
    }
  };

  const renderHeatmap = () => {
    const canvas = canvasRef.current;
    if (!canvas || heatmapData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to viewport
    const width = 1200;
    const height = 2400;
    canvas.width = width;
    canvas.height = height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Find max intensity for normalization
    const maxIntensity = Math.max(...heatmapData.map((d) => d.interaction_count));

    // Draw heatmap points
    heatmapData.forEach((point) => {
      const x = point.x_bucket * 20;
      const y = point.y_bucket * 20;
      const intensity = point.interaction_count / maxIntensity;

      // Create radial gradient for each point
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 30);

      // Color from transparent to red/orange based on intensity
      const alpha = Math.min(intensity * 0.8, 0.8);
      if (intensity > 0.7) {
        gradient.addColorStop(0, `rgba(255, 0, 0, ${alpha})`);
        gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
      } else if (intensity > 0.4) {
        gradient.addColorStop(0, `rgba(255, 140, 0, ${alpha})`);
        gradient.addColorStop(1, 'rgba(255, 140, 0, 0)');
      } else {
        gradient.addColorStop(0, `rgba(255, 255, 0, ${alpha})`);
        gradient.addColorStop(1, 'rgba(255, 255, 0, 0)');
      }

      ctx.fillStyle = gradient;
      ctx.fillRect(x - 30, y - 30, 60, 60);
    });
  };

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      loadHeatmapData();
    }
  }, [selectedPage, eventType]);

  useEffect(() => {
    if (heatmapData.length > 0) {
      renderHeatmap();
    }
  }, [heatmapData]);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'click':
        return <MousePointer2 className="h-4 w-4" />;
      case 'mousemove':
        return <MousePointer2 className="h-4 w-4" />;
      case 'scroll':
        return <ScrollText className="h-4 w-4" />;
      default:
        return <Flame className="h-4 w-4" />;
    }
  };

  const totalInteractions = heatmapData.reduce((sum, d) => sum + d.interaction_count, 0);
  const hotspots = heatmapData
    .sort((a, b) => b.interaction_count - a.interaction_count)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Flame className="h-6 w-6 text-orange-500" />
            Heatmap Visualization
          </h2>
          <p className="text-muted-foreground">Click, scroll, and interaction patterns</p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => loadHeatmapData()}
          disabled={rendering}
        >
          <RefreshCw className={`h-4 w-4 ${rendering ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Select Page</label>
              <Select value={selectedPage} onValueChange={setSelectedPage} disabled={loading}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a page..." />
                </SelectTrigger>
                <SelectContent>
                  {pages.map((page) => (
                    <SelectItem key={page.path} value={page.path}>
                      {page.path}{' '}
                      <span className="text-muted-foreground">({page.views} views)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-48">
              <label className="text-sm font-medium mb-2 block">Event Type</label>
              <Select value={eventType} onValueChange={(v) => setEventType(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="click">Clicks Only</SelectItem>
                  <SelectItem value="mousemove">Mouse Movement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="heatmap" className="space-y-4">
        <TabsList>
          <TabsTrigger value="heatmap">Heatmap View</TabsTrigger>
          <TabsTrigger value="hotspots">Top Hotspots</TabsTrigger>
        </TabsList>

        <TabsContent value="heatmap" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Interaction Heatmap - {selectedPage || 'Select a page'}</CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {getEventIcon(eventType)}
                <span>{totalInteractions.toLocaleString()} total interactions</span>
              </div>
            </CardHeader>
            <CardContent>
              {rendering ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : heatmapData.length === 0 ? (
                <div className="text-center p-12 text-muted-foreground">
                  <Flame className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No heatmap data available for this page yet.</p>
                  <p className="text-sm mt-2">
                    Start tracking visitor interactions to see patterns.
                  </p>
                </div>
              ) : (
                <div className="relative border rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-900">
                  <canvas
                    ref={canvasRef}
                    className="w-full h-auto"
                    style={{ maxHeight: '800px' }}
                  />

                  {/* Legend */}
                  <div className="absolute top-4 right-4 bg-card p-3 rounded-lg shadow-lg border">
                    <p className="text-xs font-semibold mb-2">Intensity</p>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: 'rgba(255, 0, 0, 0.8)' }}
                        ></div>
                        <span className="text-xs">High</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: 'rgba(255, 140, 0, 0.8)' }}
                        ></div>
                        <span className="text-xs">Medium</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded"
                          style={{ backgroundColor: 'rgba(255, 255, 0, 0.8)' }}
                        ></div>
                        <span className="text-xs">Low</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hotspots" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top 5 Hotspots</CardTitle>
              <p className="text-sm text-muted-foreground">
                Most interacted areas on {selectedPage}
              </p>
            </CardHeader>
            <CardContent>
              {hotspots.length === 0 ? (
                <p className="text-center text-muted-foreground p-8">No hotspot data available</p>
              ) : (
                <div className="space-y-3">
                  {hotspots.map((hotspot, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="text-lg font-bold">
                          #{index + 1}
                        </Badge>
                        <div>
                          <div className="flex items-center gap-2">
                            {getEventIcon(hotspot.event_type)}
                            <span className="font-medium capitalize">{hotspot.event_type}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Position: ({hotspot.x_bucket * 20}px, {hotspot.y_bucket * 20}px)
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">{hotspot.interaction_count}</div>
                        <div className="text-xs text-muted-foreground">interactions</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
